import { useState } from "react";
import { Box, Button, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import CustomModal from "../ui/modal/modal.component";
import JobServiceForm from "./jobServiceForm.component";
import { JOB_SERVICES_ENDPOINT } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { JobStatusTimeline, JobTaskServiceEntry } from "../../types/jobs";
import { DECIMAL_TWO_PLACES_INPUT_REGEX, formatDecimalForInput, formatQuantity, toApiDecimal } from "../../utils";

/** A service picked for a task, with how much of it was performed as typed
 *  into the form ("2,5"). */
export type TaskServiceOption = OptionRecord & { quantity: string };

// Units a service billed by the hour is entered in - the ones the "time
// worked" hint is offered for.
const HOUR_UNITS = new Set(["h", "godz", "godzina", "godziny", "godzin", "rbh", "hr", "hrs", "hour", "hours"]);

const isHourUnit = (unit: unknown) =>
  typeof unit === "string" && HOUR_UNITS.has(unit.trim().toLowerCase().replace(/\.$/, ""));

/** The job's time "In progress", in hours rounded to the nearest quarter - what
 *  the hint offers for an hourly service. Null before any time was spent. */
export const inProgressHours = (timeline?: JobStatusTimeline | null): number | null => {
  const seconds = timeline?.totals?.find((total) => total.status === "IP")?.duration_seconds ?? 0;
  if (seconds <= 0) return null;

  return Math.max(0.25, Math.round((seconds / 3600) * 4) / 4);
};

/** The task's services as the form edits them. */
export const toTaskServiceOptions = (services?: Array<JobTaskServiceEntry | OptionRecord> | null): TaskServiceOption[] =>
  (services ?? []).map((service) => ({
    ...service,
    quantity: formatDecimalForInput(service.quantity ?? "1") || "1",
  }));

/** What the API takes next to the service ids: `{service id: quantity}`. An
 *  empty field goes as 1, the quantity every service starts with. */
export const toServiceQuantities = (services: TaskServiceOption[]): Record<string, string> =>
  Object.fromEntries(
    services.map((service) => [String(service.id), toApiDecimal(service.quantity || "1")])
  );

interface TaskServicesFieldProps {
  value: TaskServiceOption[];
  onChange: (value: TaskServiceOption[]) => void;
  /** Hours the job spent "In progress" - offered for services billed by the hour. */
  hoursWorked?: number | null;
  disabled?: boolean;
  error?: string;
  label?: string;
  showLabelInInput?: boolean;
  /** Offers "Add new service" in the list (only with `service.add_jobservice`) -
   *  the job form does, the status change window does not. */
  allowAddService?: boolean;
}

/** Services performed on one task, each with its quantity - two and a half
 *  hours of technician work is the service once, quantity 2,5. Shared by the job
 *  form and the status change window so both record the same thing. */
export const TaskServicesField = ({
  value,
  onChange,
  hoursWorked,
  disabled,
  error,
  label,
  showLabelInInput = true,
  allowAddService = false,
}: TaskServicesFieldProps) => {
  const { t } = useTranslation();
  const canAddService = useHasPermission("service.add_jobservice");
  const [addServiceOpened, setAddServiceOpened] = useState(false);

  const handleServicesChange = (picked: OptionRecord[]) => {
    // Services already picked keep what was typed for them; new ones start at 1.
    const quantities = new Map(value.map((service) => [String(service.id), service.quantity]));
    onChange(
      picked.map((service) => ({
        ...service,
        quantity: quantities.get(String(service.id)) ?? "1",
      }))
    );
  };

  // A service just added to the catalogue is picked straight away, at quantity 1.
  const handleServiceCreated = (service?: OptionRecord) => {
    setAddServiceOpened(false);
    if (!service || value.some((picked) => String(picked.id) === String(service.id))) return;
    onChange([...value, { ...service, quantity: "1" }]);
  };

  const setQuantity = (serviceId: string | number, quantity: string) => {
    onChange(
      value.map((service) => (String(service.id) === String(serviceId) ? { ...service, quantity } : service))
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* A plain block holding the picker and its modal, so the modal's
          (closed) wrapper adds no gap to this column. */}
      <Box>
        <CustomAutocomplete
          multiple
          // A service is on a task at most once - how much of it was done is
          // its quantity - so a picked one leaves the list.
          filterSelectedOptions
          apiConfig={{ url: JOB_SERVICES_ENDPOINT }}
          size="small"
          value={value}
          onChange={(_, picked) => handleServicesChange(picked as OptionRecord[])}
          getOptionLabel={(option) => option.title}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={showLabelInInput ? label ?? t("Services") : undefined}
              size="small"
              error={Boolean(error)}
              helperText={error || ""}
            />
          )}
          disabled={disabled}
          addNew={
            allowAddService && canAddService
              ? { label: t("Add new service"), onClick: () => setAddServiceOpened(true) }
              : null
          }
        />
        {allowAddService && (
          <CustomModal
            opened={addServiceOpened}
            setOpened={setAddServiceOpened}
            title={t("Add new service")}
            content={<JobServiceForm onSuccess={handleServiceCreated} />}
          />
        )}
      </Box>
      {value.length > 0 && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 1.5,
              py: 0.5,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t("Task service")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("Quantity")}
            </Typography>
          </Box>
          {value.map((service, index) => {
            const offersHours = isHourUnit(service.unit) && hoursWorked != null;
            return (
              <Box
                key={service.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderTop: index > 0 ? "1px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 140px", minWidth: 0 }}>
                  {/* A list bullet in the text colour - black, white in dark mode. */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: "text.primary",
                    }}
                  />
                  <Typography variant="body2" noWrap title={service.title}>
                    {service.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                  {offersHours && (
                    <Tooltip title={t("Time the job spent in progress, to the nearest quarter of an hour.")}>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<AccessTimeIcon fontSize="small" />}
                        onClick={() => setQuantity(service.id, formatDecimalForInput(hoursWorked))}
                        disabled={disabled}
                        // Not upper-cased like other buttons: it carries a unit ("h").
                        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                      >
                        {t("Insert time worked: {{hours}} h", { hours: formatQuantity(hoursWorked) })}
                      </Button>
                    </Tooltip>
                  )}
                  <TextField
                    size="small"
                    value={service.quantity ?? ""}
                    onChange={(event) => {
                      const next = event.target.value.replace(/[\s\u00A0\u202F]/g, "");
                      if (next.startsWith("-") || !DECIMAL_TWO_PLACES_INPUT_REGEX.test(next)) return;
                      setQuantity(service.id, next);
                    }}
                    inputProps={{
                      inputMode: "decimal",
                      "aria-label": `${t("Quantity")}: ${service.title}`,
                      style: { textAlign: "right" },
                    }}
                    InputProps={{
                      endAdornment: service.unit ? (
                        <InputAdornment position="end">{service.unit}</InputAdornment>
                      ) : undefined,
                    }}
                    disabled={disabled}
                    sx={{
                      width: 120,
                      // The unit gets the number's line height (body1's is a pixel
                      // taller), so the two share one baseline in every browser.
                      "& .MuiInputAdornment-root .MuiTypography-root": { lineHeight: "inherit" },
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TaskServicesField;
